import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API_BASE, formatApiError } from "@/lib/api";
import { LOGO_URL, COMPANY } from "@/lib/constants";

export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (!identifier.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/password-reset/request`, { email: identifier.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#FAFAF5]">
      <div className="w-full max-w-sm">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B5D] hover:text-[#6B7F3E] mb-8" data-testid="forgot-back-link">
          <ArrowLeft className="w-4 h-4" /> Zurück zum Login
        </Link>
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO_URL} alt="" className="h-12 w-auto" />
          <div>
            <div className="font-display text-lg font-semibold text-[#1A201A]">{COMPANY.name}</div>
            <div className="text-xs text-[#6B7F3E]">Passwort zurücksetzen</div>
          </div>
        </div>

        {sent ? (
          <div data-testid="forgot-success" className="bg-white rounded-2xl border border-[#E5E7EB] p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-[#E8ECE1] text-[#6B7F3E] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7" strokeWidth={1.5} />
            </div>
            <h2 className="font-display text-lg font-semibold text-[#1A201A] mb-2">E-Mail verschickt</h2>
            <p className="text-sm text-[#5C6B5D] leading-relaxed">
              Falls dieser Benutzer existiert, wurde ein Reset-Link an
              <br />
              <span className="text-[#2C3E2D] font-medium">info@blessing-dienstleistungen.de</span>
              <br />
              gesendet. Öffnen Sie dort die E-Mail und klicken Sie auf „Neues Passwort setzen".
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-semibold text-[#1A201A] tracking-tight mb-2">
              Passwort vergessen?
            </h1>
            <p className="text-sm text-[#5C6B5D] mb-6">
              Geben Sie Ihren Benutzernamen ein. Wir schicken Ihnen einen Reset-Link an das
              Postfach <span className="text-[#2C3E2D] font-medium">info@blessing-dienstleistungen.de</span>.
            </p>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="identifier">Benutzername</Label>
                <Input
                  id="identifier" data-testid="forgot-identifier-input" type="text" required
                  value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                  className="mt-1.5" placeholder="z. B. m.blessing"
                  autoComplete="username"
                />
              </div>
              <Button
                type="submit" data-testid="forgot-submit-btn"
                disabled={busy}
                className="w-full bg-[#6B7F3E] hover:bg-[#5A6B34] text-white h-10"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1.5" /> Reset-Link senden</>}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
